/**
 * Talent Cloud — Dashboard
 * Roster health overview for Talent Managers.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sidebar } from '../../components/Sidebar';
import { Users, TrendingUp, Award, Activity, FileText, AlertCircle, IndianRupee, ChevronRight, Instagram, AlertTriangle, ZapOff, Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n);
const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const AVAILABILITY_LABEL = {
  available: { label: 'Available', color: '#00C972' },
  booked: { label: 'Booked', color: '#FF451A' },
  vacation: { label: 'Vacation', color: '#FFB800' },
  paused: { label: 'Paused', color: '#999999' },
  locked: { label: 'Exclusive Lock', color: '#9B6EFF' },
};

export const TalentDashboard = () => {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [dash, h] = await Promise.all([
          axios.get(`${API_URL}/api/talent/dashboard`, { withCredentials: true }),
          axios.get(`${API_URL}/api/talent/roster-health`, { withCredentials: true }),
        ]);
        setData(dash.data);
        setHealth(h.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="text-center py-20"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="talent-dashboard-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-2">Talent Cloud</div>
            <h1 className="font-display text-5xl tracking-tight text-[#0F0F0F] dark:text-white mb-2">Roster.</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Your exclusive creator portfolio — health, performance, and operational pulse.</p>
          </div>

          {/* Top stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <StatCard delay={0} icon={Users} label="Roster Size" value={data?.roster_size || 0} color="#FF451A" />
            <StatCard delay={0.05} icon={Activity} label="Active Campaigns" value={data?.active_campaigns || 0} color="#00C972" />
            <StatCard delay={0.1} icon={Award} label="Completed" value={data?.completed_campaigns || 0} color="#0055FF" />
            <StatCard delay={0.15} icon={IndianRupee} label="Roster Revenue (paid)" value={fmtINR(data?.total_revenue || 0)} color="#9B6EFF" isText />
          </div>

          {/* Availability + Pending */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-2 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6">
              <h3 className="font-display text-xl text-[#0F0F0F] dark:text-white mb-1 tracking-tight">Availability Pulse</h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-5">live across your roster</p>
              <div className="grid grid-cols-5 gap-3">
                {Object.entries(data?.availability || {}).map(([key, count]) => {
                  const meta = AVAILABILITY_LABEL[key] || { label: key, color: '#666' };
                  return (
                    <div key={key} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3 text-center">
                      <div className="font-display text-3xl text-[#0F0F0F] dark:text-white mb-1" style={{ color: meta.color }}>{count}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500">{meta.label}</div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-gradient-to-br from-[#FFB800]/10 to-[#0F0F0F] border border-[#FFB800]/30 rounded-2xl p-6">
              <h3 className="font-display text-xl text-[#0F0F0F] dark:text-white mb-1 tracking-tight flex items-center gap-2"><AlertCircle size={16} className="text-[#FFB800]" /> Pending Media Kits</h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB800] mb-4">{data?.pending_media_kits_count || 0} creators need updates</p>
              {(data?.pending_media_kits || []).length === 0 ? (
                <p className="text-sm text-[#6B6B6B] dark:text-zinc-500">All media kits are up to date.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.pending_media_kits.slice(0, 5).map((c) => (
                    <Link key={c.id} to={`/talent/creators/${c.id}`} className="flex items-center justify-between p-2 rounded-lg hover:bg-white/5 transition-colors text-sm text-[#0F0F0F] dark:text-white" data-testid="pending-mediakit-row">
                      {c.name} <ChevronRight size={14} className="text-[#6B6B6B] dark:text-zinc-500" />
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Roster Health */}
          {health && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 mb-6">
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <h3 className="font-display text-xl text-[#0F0F0F] dark:text-white tracking-tight">Roster Health</h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">operational signals across {health.roster_size} creators</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <HealthCard icon={ZapOff} label="Inactive (60d)" count={health.inactive_count} color="#FF3B30" items={health.inactive} />
                <HealthCard icon={Camera} label="Missing media kit" count={health.missing_media_kits_count} color="#FFB800" items={health.missing_media_kits} />
                <HealthCard icon={AlertTriangle} label="Low engagement (<1%)" count={health.low_engagement_count} color="#9B6EFF" items={health.low_engagement} />
                <HealthCard icon={AlertCircle} label="No active campaigns" count={health.no_active_campaigns_count} color="#0055FF" items={health.no_active_campaigns} />
              </div>
            </motion.div>
          )}

          {/* Top performers */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-xl text-[#0F0F0F] dark:text-white tracking-tight">Top Performers</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">ranked by reach</p>
              </div>
              <Link to="/talent/roster" className="text-sm text-[#FF451A] hover:text-white transition-colors font-medium" data-testid="view-full-roster-link">View full roster →</Link>
            </div>
            <div className="space-y-2">
              {(data?.top_performers || []).map((c) => {
                const meta = AVAILABILITY_LABEL[c.availability_status] || AVAILABILITY_LABEL.available;
                return (
                  <Link key={c.id} to={`/talent/creators/${c.id}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] border border-transparent hover:border-white/5 transition-all group" data-testid="top-performer-row">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF451A] to-[#9B6EFF] flex items-center justify-center text-[#0F0F0F] dark:text-white font-bold">{c.name?.[0]?.toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[#0F0F0F] dark:text-white font-medium group-hover:text-[#FF451A] transition-colors">{c.name}</div>
                      <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 flex items-center gap-1"><Instagram size={10} /> @{c.instagram_handle || '—'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#0F0F0F] dark:text-white font-mono">{fmtNum(c.instagram_followers || 0)}</div>
                      <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</div>
                    </div>
                    <div className="flex flex-wrap gap-1 max-w-[200px] justify-end">
                      {(c.categories || []).slice(0, 2).map((cat) => (
                        <span key={cat} className="text-[10px] bg-white/5 border border-black/[0.10] dark:border-white/10 text-[#6B6B6B] dark:text-zinc-400 px-2 py-0.5 rounded-full">{cat}</span>
                      ))}
                    </div>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const HealthCard = ({ icon: Icon, label, count, color, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-black/30 border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid={`health-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 flex-1">{label}</div>
      </div>
      <div className="font-display text-2xl" style={{ color }}>{count}</div>
      {count > 0 && (
        <button onClick={() => setOpen(!open)} className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 hover:text-white mt-1" data-testid="health-toggle">
          {open ? 'hide' : 'view'} →
        </button>
      )}
      {open && items && items.length > 0 && (
        <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
          {items.slice(0, 6).map((c) => (
            <Link key={c.id} to={`/talent/creators/${c.id}`} className="block text-xs text-zinc-300 hover:text-[#FF451A] truncate" data-testid="health-creator-link">{c.name}</Link>
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, isText, delay }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 relative overflow-hidden">
    <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full blur-3xl opacity-30" style={{ backgroundColor: color }} />
    <div className="relative">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg border flex items-center justify-center" style={{ borderColor: `${color}40`, backgroundColor: `${color}15` }}>
          <Icon size={14} style={{ color }} />
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500">{label}</div>
      </div>
      <div className={`font-display ${isText ? 'text-2xl' : 'text-4xl'} text-[#0F0F0F] dark:text-white tracking-tight`}>{value}</div>
    </div>
  </motion.div>
);
